import { isEqual, omit } from 'lodash';
import { useMemo } from 'react';

export interface UseIsFilteredOptions<TQuery extends Record<string, unknown>> {
  query: TQuery;
  defaultQuery: TQuery;
  /** Se `true`, ritorna sempre `false` (anti-flicker durante il refetch). */
  isFetching?: boolean;
  /** Chiavi addizionali da escludere dal confronto. */
  ignoredKeys?: Array<keyof TQuery>;
}

/**
 * `query` ≠ `defaultQuery` ignorando paginazione/ordinamenti/limite e
 * `__cacheInvalidator` (marker tecnico di cache-busting che non rappresenta un
 * filtro applicato dall'utente).
 */
export function useIsFiltered<TQuery extends Record<string, unknown>>({
  query,
  defaultQuery,
  isFetching,
  ignoredKeys = [],
}: UseIsFilteredOptions<TQuery>): boolean {
  return useMemo(() => {
    if (isFetching) return false;
    const omitKeys = [
      'pagina',
      'ordinamenti',
      'limite',
      '__cacheInvalidator',
      ...(ignoredKeys as string[]),
    ];
    return !isEqual(omit(query, omitKeys), omit(defaultQuery, omitKeys));
  }, [isFetching, query, defaultQuery, ignoredKeys]);
}
